import { useEffect, useMemo, useState } from "react";
import { ContenidosPlanes } from "./ContenidosPlanes";
import "./paneles.css";

/**
 * Contenidos: el calendario del mes y cómo va cada artículo.
 *
 * ── Trello dice qué hay que hacer; esto dice cómo va ────────────────────────
 *
 * Lo fijó Jorge el 25/09/2026. Aquí no se abre trabajo ni se aprueba nada: se
 * mira. Por eso no hay ningún botón de «crear», y los que hay salen de lo que
 * la propia línea dice que se puede hacer.
 *
 * ── Dos vistas del mismo mes, y por qué las dos ────────────────────────────
 *
 * El **calendario** responde a «¿qué sale esta semana?», que es la pregunta de
 * un lunes: hay que verlo repartido en los días. La **lista** responde a «¿qué
 * está atascado?», y para eso el calendario es malísimo — lo que no tiene fecha
 * de salida no cae en ningún día y desaparece.
 *
 * Así que van las dos, sobre los mismos datos, y el filtro es común.
 */

type Paso = "propuesto" | "aprobado" | "redactando" | "en revisión" | "programado" | "publicado" | "retirado";

interface ComoVa {
  paso: Paso;
  cuando: string | null;
  queEsLaFecha: "sale" | "salió" | "retirado" | "para" | null;
  enlace: string | null;
  porQue: string | null;
  acciones: string[];
}

interface Linea {
  id: string;
  clienteId: string;
  cliente: string;
  titulo: string;
  keyword: string | null;
  canal: string;
  dia: string | null;
  comoVa: ComoVa;
  mesDelPlan: string | null;
  /** El enlace al editor de WordPress, cuando ya está creada allí. */
  editarEnWordpress?: string | null;
  imagen?: { titulo: string; url: string; origen: "pixabay" | "pexels" | "ia" } | null;
  buscarEnPexels?: string | null;
}

interface Respuesta {
  mes: string;
  lineas: Linea[];
  clientes: { id: string; nombre: string }[];
  porPaso: { paso: Paso; cuantos: number }[];
  nota: string | null;
}

/** El mismo color que usa el servidor, y por el mismo motivo: uno por paso. */
const COLOR: Record<Paso, string> = {
  propuesto: "#8b8f98",
  aprobado: "#4b8bd6",
  redactando: "#b07ad6",
  "en revisión": "#d69b3f",
  programado: "#3fa7a0",
  publicado: "#3fa15c",
  retirado: "#a04a4a",
};

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const mesEnLetra = (mes: string): string => {
  const [anio, m] = mes.split("-").map(Number) as [number, number];
  return `${MESES[m - 1]} de ${anio}`;
};

const mesVecino = (mes: string, cuantos: number): string => {
  const [anio, m] = mes.split("-").map(Number) as [number, number];
  const d = new Date(Date.UTC(anio, m - 1 + cuantos, 1));
  return d.toISOString().slice(0, 7);
};

/**
 * Los días del mes, empezando en lunes.
 *
 * Se rellena con los huecos de delante para que las columnas sean los días de
 * la semana. Sin eso, el día 1 cae donde toque y el calendario deja de servir
 * para ver una semana de un vistazo, que es para lo que está.
 */
function laRejilla(mes: string): (string | null)[] {
  const [anio, m] = mes.split("-").map(Number) as [number, number];
  const primero = new Date(Date.UTC(anio, m - 1, 1));
  const cuantos = new Date(Date.UTC(anio, m, 0)).getUTCDate();
  /* getUTCDay: 0 es domingo. Aquí la semana empieza en lunes. */
  const huecos = (primero.getUTCDay() + 6) % 7;
  return [
    ...Array.from({ length: huecos }, () => null),
    ...Array.from({ length: cuantos }, (_, i) => `${mes}-${String(i + 1).padStart(2, "0")}`),
  ];
}

/** Qué acción se le está preguntando a qué artículo, dentro de su tarjeta. */
type Pregunta = { id: string; que: "retirar" | "fecha" } | null;

export function Contenidos({ clienteId, irA }: { clienteId?: string; irA?: (pantalla: string) => void } = {}) {
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  /** Calendario (lo que hay) o Planes del mes (crearlo). Sólo en la pantalla general. */
  const [vista, setVista] = useState<"calendario" | "planes">("calendario");
  const [datos, setDatos] = useState<Respuesta | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<string | null>(null);
  const [cliente, setCliente] = useState(clienteId ?? "");
  const [paso, setPaso] = useState<Paso | "">("");
  const [leyendo, setLeyendo] = useState<{ titulo: string; texto: string | null; nota: string | null } | null>(null);
  const [dice, setDice] = useState<string | null>(null);
  const [vuelta, setVuelta] = useState(0);
  const [pregunta, setPregunta] = useState<Pregunta>(null);
  const [nuevaFecha, setNuevaFecha] = useState("");

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setFallo(null);
    fetch(`/api/contenidos?mes=${mes}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`el servidor contestó ${r.status}`))))
      .then((d: Respuesta) => vivo && setDatos(d))
      .catch((e: Error) => vivo && setFallo(e.message))
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [mes, vuelta]);

  const lineas = useMemo(() => {
    const todas = datos?.lineas ?? [];
    return todas.filter((l) => (!cliente || l.clienteId === cliente) && (!paso || l.comoVa.paso === paso));
  }, [datos, cliente, paso]);

  const porDia = useMemo(() => {
    const mapa = new Map<string, Linea[]>();
    for (const l of lineas) {
      if (!l.dia) continue;
      const ya = mapa.get(l.dia);
      if (ya) ya.push(l);
      else mapa.set(l.dia, [l]);
    }
    return mapa;
  }, [lineas]);

  /*
   * Lo que no cae en ningún día se cuenta aparte y se dice.
   *
   * Un artículo sin fecha no sale en el calendario, y si no se dice parece que
   * no existe: el recuento de arriba diría siete y en la rejilla habría cinco.
   */
  const sinFecha = lineas.filter((l) => !l.dia).length;
  const hoy = new Date().toISOString().slice(0, 10);

  /*
   * Cada acción recarga el mes al terminar.
   *
   * Y no se toca la línea a mano: lo que decide qué se puede hacer con un
   * artículo lo calcula el servidor, así que después de cambiarlo hay que
   * volver a preguntárselo. Pintarlo aquí de memoria es cómo el panel empieza a
   * decir una cosa y la web otra.
   */
  /** El artículo que se está arrastrando a otro día, y el que se ha pinchado en el calendario. */
  const [arrastrando, setArrastrando] = useState<string | null>(null);
  const [encima, setEncima] = useState<string | null>(null);
  const [elegido, setElegido] = useState<string | null>(null);

  const hacer = async (id: string, que: string, cuerpo?: unknown) => {
    setDice(null);
    try {
      const r = await fetch(`/api/contenidos/${id}/${que}`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(cuerpo ?? {}),
      });
      const d = (await r.json()) as { dice?: string; error?: string };
      setDice(d.dice ?? d.error ?? "no ha contestado nada");
      setPregunta(null);
      if (r.ok) setVuelta((v) => v + 1);
    } catch (e) {
      setDice(`no se ha podido: ${(e as Error).message}`);
    }
  };

  const leer = async (id: string, titulo: string) => {
    setDice(null);
    try {
      const r = await fetch(`/api/contenidos/${id}`, { credentials: "include" });
      const d = (await r.json()) as { texto?: string | null; nota?: string | null };
      setLeyendo({ titulo, texto: d.texto ?? null, nota: d.nota ?? null });
    } catch (e) {
      setDice(`no se ha podido leer: ${(e as Error).message}`);
    }
  };

  /* Dentro de la ficha de un cliente va sin márgenes de pantalla ni título. */
  const raiz = clienteId ? "pnl ct-dentro" : "pnl";

  if (cargando && !datos)
    return (
      <div className={raiz}>
        <p className="pnl-sub">Mirando el calendario de contenidos…</p>
      </div>
    );
  if (fallo)
    return (
      <div className={raiz}>
        <div className="pnl-nota es-mal" role="alert">
          <p>No se ha podido leer el calendario de contenidos: {fallo}. Lo que veas de antes puede estar viejo.</p>
        </div>
      </div>
    );

  const total = (datos?.porPaso ?? []).reduce((a, p) => a + p.cuantos, 0);

  const conmutador = !clienteId && (
    <div className="pnl-vistas" role="group" aria-label="Qué ver">
      <button aria-pressed={vista === "calendario"} onClick={() => setVista("calendario")}>
        Calendario
      </button>
      <button aria-pressed={vista === "planes"} onClick={() => setVista("planes")}>
        Planes del mes
      </button>
    </div>
  );

  if (vista === "planes" && !clienteId)
    return (
      <section className={raiz}>
        <header className="pnl-cabecera">
          <div>
            <h1>Contenidos</h1>
            <p className="pnl-sub">Prepara el plan de artículos de cada cliente y pide el sí desde aquí.</p>
          </div>
          {conmutador}
        </header>
        <ContenidosPlanes {...(irA ? { irA } : {})} />
      </section>
    );

  return (
    <section className={raiz}>
      <header className="pnl-cabecera">
        <div>
          {!clienteId && <h1>Contenidos</h1>}
          <p className="pnl-sub">
            {total === 0 ? "Nada apuntado este mes" : `${total} ${total === 1 ? "artículo" : "artículos"} este mes`}
            {!clienteId && " · los planes se preparan en «Planes del mes»"}
          </p>
        </div>
        {conmutador}
        <div className="ct-mes" role="group" aria-label="Mes">
          <button type="button" onClick={() => setMes(mesVecino(mes, -1))} aria-label="El mes anterior">
            ‹
          </button>
          <span>{mesEnLetra(mes)}</span>
          <button type="button" onClick={() => setMes(mesVecino(mes, 1))} aria-label="El mes siguiente">
            ›
          </button>
        </div>
      </header>

      {/* La escalera entera, con los ceros: un peldaño que falta se lee como que no existe. Pinchar filtra. */}
      <div className="pnl-filtros" role="group" aria-label="Filtrar por estado">
        <button className="pnl-chip" aria-pressed={!paso} onClick={() => setPaso("")}>
          Todos <span className="n">{total}</span>
        </button>
        {(datos?.porPaso ?? []).map((p) => (
          <button
            key={p.paso}
            className={`pnl-chip${p.cuantos === 0 ? " ct-cero" : ""}`}
            aria-pressed={paso === p.paso}
            onClick={() => setPaso(paso === p.paso ? "" : p.paso)}
          >
            <span className="ct-punto" style={{ background: COLOR[p.paso] }} />
            {p.paso} <span className="n">{p.cuantos}</span>
          </button>
        ))}
        {!clienteId && (datos?.clientes.length ?? 0) > 0 && (
          <select
            className="pnl-select"
            value={cliente}
            onChange={(e) => setCliente(e.target.value)}
            aria-label="Filtrar por cliente"
          >
            <option value="">Todos los clientes</option>
            {(datos?.clientes ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        )}
      </div>

      {datos?.nota && lineas.length > 0 && <p className="pnl-sub">{datos.nota}</p>}

      {/* Lo que contestó la última acción, tal cual: si dijo que no, aquí está el porqué. */}
      {dice && (
        <div className="pnl-nota" role="status">
          <p>{dice}</p>
          <button aria-label="Cerrar" onClick={() => setDice(null)}>
            ×
          </button>
        </div>
      )}

      {leyendo && (
        <section className="pnl-seccion">
          <header>
            <h2>{leyendo.titulo}</h2>
            <button type="button" className="pnl-boton ir" onClick={() => setLeyendo(null)}>
              Cerrar
            </button>
          </header>
          {leyendo.nota && <p className="pnl-vacio">{leyendo.nota}</p>}
          {leyendo.texto && <div className="ct-texto">{leyendo.texto}</div>}
        </section>
      )}

      {lineas.length === 0 ? (
        <div className="tp2-vacio">
          <strong>Nada en {mesEnLetra(mes)}</strong>
          {paso || cliente
            ? "Con este filtro no hay nada. Quítalo para ver el mes entero."
            : (datos?.nota ?? "El plan del mes lo abre el director, y hasta que no se aprueba no aparece nada aquí.")}
        </div>
      ) : (
        <>
          <div className="ct-calendario">
            {["lun", "mar", "mié", "jue", "vie", "sáb", "dom"].map((d) => (
              <div key={d} className="ct-dia-semana">
                {d}
              </div>
            ))}
            {laRejilla(mes).map((dia, i) =>
              dia ? (
                <div
                  key={dia}
                  className={`ct-dia${dia === hoy ? " es-hoy" : ""}${encima === dia ? " es-destino" : ""}`}
                  /*
                   * Soltar aquí un artículo le cambia la fecha (24/10/2026): la misma
                   * acción que «Cambiar fecha», que si ya estaba programado en su
                   * WordPress lo quita y lo vuelve a programar en el día nuevo.
                   */
                  onDragOver={(e) => {
                    if (!arrastrando) return;
                    e.preventDefault();
                    if (encima !== dia) setEncima(dia);
                  }}
                  onDragLeave={() => setEncima((x) => (x === dia ? null : x))}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = arrastrando;
                    setArrastrando(null);
                    setEncima(null);
                    const l = lineas.find((x) => x.id === id);
                    if (id && l && l.dia !== dia) void hacer(id, "fecha", { dia });
                  }}
                >
                  <span className="ct-numero">{Number(dia.slice(-2))}</span>
                  {(porDia.get(dia) ?? []).map((l) => {
                    const sePuedeMover = l.comoVa.acciones.includes("cambiar fecha");
                    return (
                      <button
                        type="button"
                        key={l.id}
                        className={`ct-evento${elegido === l.id ? " es-elegido" : ""}${sePuedeMover ? " se-mueve" : ""}`}
                        title={`${clienteId ? "" : `${l.cliente} · `}${l.titulo} · ${l.comoVa.paso}${sePuedeMover ? " · arrástralo a otro día para moverlo" : ""}`}
                        style={{ borderLeftColor: COLOR[l.comoVa.paso], ["--c" as string]: COLOR[l.comoVa.paso] }}
                        draggable={sePuedeMover}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", l.id);
                          setArrastrando(l.id);
                        }}
                        onDragEnd={() => {
                          setArrastrando(null);
                          setEncima(null);
                        }}
                        onClick={() => {
                          setElegido(l.id);
                          document.getElementById(`art-${l.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                        }}
                      >
                        {l.imagen?.url && <img src={l.imagen.url} alt="" loading="lazy" />}
                        <span className="ct-evento-texto">
                          {!clienteId && <span className="ct-evento-cliente">{l.cliente}</span>}
                          {l.titulo}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div key={`hueco-${i}`} className="ct-hueco" />
              ),
            )}
          </div>

          <p className="pnl-sub ct-pista">
            Pincha un artículo para ver su ficha. Los que aún no han salido se pueden arrastrar a otro día para
            cambiarles la fecha.
          </p>

          {sinFecha > 0 && (
            <p className="pnl-sub">
              {sinFecha === 1
                ? "Un artículo no tiene fecha, así que no sale en el calendario. Está abajo."
                : `${sinFecha} artículos no tienen fecha, así que no salen en el calendario. Están abajo.`}
            </p>
          )}

          <ul className="tp2-lista">
            {lineas.map((l) => (
              <li
                key={l.id}
                id={`art-${l.id}`}
                className={`tp2-tarjeta ct-articulo${elegido === l.id ? " es-elegido" : ""}`}
                style={{ ["--riesgo" as string]: COLOR[l.comoVa.paso] }}
              >
                <div className="tp2-arriba">
                  {!clienteId && <span className="tp2-cliente">{l.cliente}</span>}
                  {l.keyword && (
                    <span className="tp2-donde" title="La búsqueda a la que ataca">
                      «{l.keyword}»
                    </span>
                  )}
                  <span className="tp2-plazo ct-paso" style={{ color: COLOR[l.comoVa.paso], borderColor: COLOR[l.comoVa.paso] }}>
                    {l.comoVa.paso}
                  </span>
                </div>
                <p className="tp2-que">{l.titulo}</p>
                <div className="tp2-etiquetas">
                  <span className="tp2-etiqueta">{l.canal}</span>
                  {l.comoVa.cuando && (
                    <span className="tp2-etiqueta">
                      {l.comoVa.queEsLaFecha} el {l.comoVa.cuando}
                    </span>
                  )}
                </div>
                {/*
                  El porqué entero: es lo que dice qué hacer con lo atascado, y
                  recortarlo deja «no pasó la revisión:» sin el motivo.
                */}
                {l.comoVa.porQue && <p className="tp2-aviso">{l.comoVa.porQue}</p>}

                <div className="tp2-abajo">
                  {l.comoVa.enlace && (
                    <a className="pnl-boton plano" href={l.comoVa.enlace} target="_blank" rel="noreferrer">
                      Leerlo en su web ↗
                    </a>
                  )}
                  {l.editarEnWordpress && (
                    <a className="pnl-boton plano" href={l.editarEnWordpress} target="_blank" rel="noreferrer">
                      Editar en WordPress ↗
                    </a>
                  )}
                  {/*
                    La imagen destacada: la foto del cliente que puso el guion, o si
                    ninguna encajaba, Pexels para elegir una y ponerla a mano.
                  */}
                  {l.imagen &&
                    (l.imagen.url ? (
                      <a className="ct-imagen" href={l.imagen.url} target="_blank" rel="noreferrer" title={l.imagen.titulo}>
                        <img src={l.imagen.url} alt="" loading="lazy" />
                        <span>Foto de {l.imagen.origen === "pexels" ? "Pexels" : "Pixabay"}</span>
                      </a>
                    ) : (
                      <span className="ct-imagen" title={l.imagen.titulo}>
                        <span>Foto hecha con IA</span>
                      </span>
                    ))}
                  {l.buscarEnPexels && (
                    <a className="pnl-boton plano" href={l.buscarEnPexels} target="_blank" rel="noreferrer">
                      {l.imagen ? "Cambiar: buscar en Pexels ↗" : "Sin foto · buscar en Pexels ↗"}
                    </a>
                  )}
                  {pregunta?.id === l.id && pregunta.que === "retirar" ? (
                    <div className="tp2-pregunta">
                      {/* Retirar quita la entrada de la web de un cliente: se pregunta. */}
                      <span className="q">¿Retirarlo de la web de {l.cliente}?</span>
                      <button className="pnl-boton plano" onClick={() => setPregunta(null)}>
                        Cancelar
                      </button>
                      <button className="pnl-boton no" onClick={() => void hacer(l.id, "retirar")}>
                        Retirar
                      </button>
                    </div>
                  ) : pregunta?.id === l.id && pregunta.que === "fecha" ? (
                    <form
                      className="tp2-pregunta"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (nuevaFecha) void hacer(l.id, "fecha", { dia: nuevaFecha });
                      }}
                    >
                      <span className="q">¿Para qué día?</span>
                      <input
                        type="date"
                        value={nuevaFecha}
                        onChange={(e) => setNuevaFecha(e.target.value)}
                        aria-label="Nueva fecha"
                        style={{ minWidth: 0, flex: "0 1 auto" }}
                      />
                      <button type="button" className="pnl-boton plano" onClick={() => setPregunta(null)}>
                        Cancelar
                      </button>
                      <button type="submit" className="pnl-boton si" disabled={!nuevaFecha}>
                        Cambiar
                      </button>
                    </form>
                  ) : (
                    <div className="tp2-botones">
                      {l.comoVa.acciones.map((a) =>
                        a === "leer" ? (
                          <button key={a} className="pnl-boton" onClick={() => void leer(l.id, l.titulo)}>
                            Leer
                          </button>
                        ) : a === "retirar" ? (
                          <button key={a} className="pnl-boton no" onClick={() => setPregunta({ id: l.id, que: "retirar" })}>
                            Retirar
                          </button>
                        ) : a === "cambiar fecha" ? (
                          <button
                            key={a}
                            className="pnl-boton"
                            onClick={() => {
                              setNuevaFecha(l.dia ?? "");
                              setPregunta({ id: l.id, que: "fecha" });
                            }}
                          >
                            Cambiar fecha
                          </button>
                        ) : (
                          <button key={a} className="pnl-boton" onClick={() => void hacer(l.id, "otra-version")}>
                            Pedir otra versión
                          </button>
                        ),
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
